// Structural inputs keep the transport calculation independent of business state.
interface SentFrameTiming {
  rtpTimestamp: number;
  firstSendMs: number;
  lastSendMs: number;
}
interface VideoClockEstimate {
  offsetMs: number;
  uncertaintyMs: number;
  measuredAt: number;
}

export interface FrameMetadata {
  expectedDisplayTime: number;
  presentationTime: number;
  presentedFrames: number;
  mediaTime: number;
  rtpTimestamp?: number;
  receiveTime?: number;
  processingDuration?: number;
}

export interface FramePathSample {
  callbackMs: number;
  rtpTimestamp: number | null;
  presentedFrames: number;
  displayMs: number;
  receiveMs: number | null;
  decodeMs: number | null;
  producerId: string | null;
  visibility: boolean;
  clock: VideoClockEstimate | null;
  firstSendMs: number | null;
  lastSendMs: number | null;
  sendMs: number | null;
  transitMs: number | null;
  transitEstimateMs: number | null;
  receiveToDisplayMs: number | null;
  backendToDisplayMs: number | null;
  callbackGapMs: number | null;
  missedCallbacks: number;
  match:
    | "pending"
    | "matched"
    | "metadata-unavailable"
    | "clock-unavailable"
    | "clock-inconsistent"
    | "timing-unavailable"
    | "hidden";
}

export interface FramePathReport {
  status: "running" | "finishing" | "complete" | "cancelled";
  startedAtIso: string;
  timeOriginMs: number;
  remainingSeconds: number;
  reason?: string;
  measuredSeconds: number;
  hiddenSeconds: number;
  decodedFps: number | null;
  presentedFps: number | null;
  callbackFps: number | null;
  maxCallbackGapMs: number | null;
  missedCallbacks: number;
  matchedFrames: number;
  unmatchedFrames: number;
  samples: FramePathSample[];
}

const DISTRIBUTION_KEYS = [
  "backendToDisplayMs",
  "transitMs",
  "receiveToDisplayMs",
  "sendMs",
  "decodeMs",
] as const;
export type FramePathSummary = Omit<FramePathReport, "samples"> & {
  distributions: Record<
    (typeof DISTRIBUTION_KEYS)[number],
    ReturnType<typeof framePathDistribution>
  >;
  clockUncertaintyMs: number | null;
};

/** Keep per-frame arrays out of React state and development render traces. */
export function framePathSummary(report: FramePathReport | null): FramePathSummary | null {
  if (!report) return null;
  const { samples, ...summary } = report;
  return {
    ...summary,
    distributions: Object.fromEntries(
      DISTRIBUTION_KEYS.map((key) => [key, framePathDistribution(report, key)])
    ) as FramePathSummary["distributions"],
    clockUncertaintyMs: samples
      .filter((s) => s.match === "matched")
      .reduce<number | null>((max, s) => Math.max(max ?? 0, s.clock?.uncertaintyMs ?? 0), null),
  };
}

const DURATION_MS = 60_000,
  WARMUP_MS = 5000,
  GRACE_MS = 3000;
const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

export function framePathDistribution(
  report: FramePathReport,
  key: "backendToDisplayMs" | "transitMs" | "receiveToDisplayMs" | "sendMs" | "decodeMs"
) {
  const values = report.samples
    .filter((s) => s.visibility)
    .flatMap((s) => (finite(s[key]) ? [s[key]] : []))
    .sort((a, b) => a - b);
  return {
    n: values.length,
    mean: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null,
    p95: values.length ? values[Math.ceil(values.length * 0.95) - 1]! : null,
    max: values.length ? values[values.length - 1]! : null,
  };
}

/** Exact RTP matching; sender telemetry may arrive AFTER the displayed video frame. */
export function createFramePathMeasurement(timeOrigin: number, now = () => performance.now()) {
  let report: FramePathReport | null = null;
  let started = 0,
    previousTick = 0,
    previousCallback: FramePathSample | null = null;
  let visibleMs = 0,
    hiddenMs = 0,
    decodedCount = 0,
    decodedMs = 0;
  let presentedCount = 0,
    previousPresentationCount: number | null = null;
  const timing = new Map<string, SentFrameTiming>();

  function match(sample: FramePathSample) {
    if (sample.match !== "pending" || sample.rtpTimestamp === null || !sample.producerId) return;
    const sent = timing.get(`${sample.producerId}/${sample.rtpTimestamp}`);
    if (!sent) return;
    if (!sample.clock || timeOrigin + sample.callbackMs - sample.clock.measuredAt > 15_000) {
      sample.match = "clock-unavailable";
      return;
    }
    const offset = sample.clock.offsetMs;
    sample.firstSendMs = sent.firstSendMs;
    sample.lastSendMs = sent.lastSendMs;
    const transit =
      sample.receiveMs === null ? null : timeOrigin + sample.receiveMs + offset - sent.lastSendMs;
    const total = timeOrigin + sample.displayMs + offset - sent.firstSendMs;
    sample.transitEstimateMs = transit;
    if (
      total < 0 ||
      total > 10_000 ||
      (transit !== null && transit < -sample.clock.uncertaintyMs)
    ) {
      sample.match = "clock-inconsistent";
      return;
    }
    sample.sendMs = sent.lastSendMs - sent.firstSendMs;
    // On a local SFU path, transit can be below the clock resolution. Preserve
    // the raw estimate, omit this segment, and still measure the exact frame total.
    sample.transitMs = transit !== null && transit >= 0 ? transit : null;
    sample.backendToDisplayMs = total;
    sample.match = "matched";
  }

  function finish(status: "complete" | "cancelled", reason?: string) {
    if (!report || report.status === "complete" || report.status === "cancelled") return;
    for (const sample of report.samples) {
      match(sample);
      if (sample.match === "pending") sample.match = "timing-unavailable";
    }
    report.status = status;
    report.reason = reason;
    report.remainingSeconds = 0;
    timing.clear();
  }

  function updateSummary() {
    if (!report) return;
    const visible = report.samples.filter((s) => s.visibility);
    report.measuredSeconds = visibleMs / 1000;
    report.hiddenSeconds = hiddenMs / 1000;
    report.decodedFps = decodedMs > 0 ? (decodedCount * 1000) / decodedMs : null;
    report.presentedFps =
      visibleMs > 0 && presentedCount > 0 ? (presentedCount * 1000) / visibleMs : null;
    report.callbackFps = visibleMs > 0 ? (visible.length * 1000) / visibleMs : null;
    report.maxCallbackGapMs = visible.reduce<number | null>(
      (max, s) => (s.callbackGapMs === null ? max : Math.max(max ?? 0, s.callbackGapMs)),
      null
    );
    report.missedCallbacks = visible.reduce((n, s) => n + s.missedCallbacks, 0);
    report.matchedFrames = visible.filter((s) => s.match === "matched").length;
    report.unmatchedFrames = visible.length - report.matchedFrames;
  }

  return {
    start() {
      started = previousTick = now();
      previousCallback = null;
      previousPresentationCount = null;
      visibleMs = hiddenMs = decodedCount = decodedMs = presentedCount = 0;
      report = {
        status: "running",
        startedAtIso: new Date(timeOrigin + started).toISOString(),
        timeOriginMs: timeOrigin,
        remainingSeconds: 60,
        measuredSeconds: 0,
        hiddenSeconds: 0,
        decodedFps: null,
        presentedFps: null,
        callbackFps: null,
        maxCallbackGapMs: null,
        missedCallbacks: 0,
        matchedFrames: 0,
        unmatchedFrames: 0,
        samples: [],
      };
    },
    ingest(producerId: string | null | undefined, frames: readonly SentFrameTiming[] = []) {
      if (!producerId) return;
      for (const frame of frames) timing.set(`${producerId}/${frame.rtpTimestamp}`, frame);
      while (timing.size > 360) timing.delete(timing.keys().next().value!);
      for (const sample of report?.samples ?? []) if (sample.match === "pending") match(sample);
    },
    frame(
      metadata: FrameMetadata,
      callbackMs: number,
      producerId: string | null | undefined,
      clock: VideoClockEstimate | null | undefined,
      visible: boolean
    ) {
      if (
        !report ||
        report.status !== "running" ||
        callbackMs - started < WARMUP_MS ||
        callbackMs - started >= DURATION_MS
      )
        return;
      const validReceive =
        finite(metadata.receiveTime) &&
        metadata.receiveTime >= 0 &&
        metadata.expectedDisplayTime >= metadata.receiveTime;
      const rtp = metadata.rtpTimestamp;
      const counter = metadata.presentedFrames;
      const gapCount =
        previousPresentationCount === null ? 1 : Math.max(0, counter - previousPresentationCount);
      previousPresentationCount = counter;
      if (visible) presentedCount += gapCount;
      const sample: FramePathSample = {
        callbackMs,
        rtpTimestamp:
          finite(rtp) && Number.isInteger(rtp) && rtp >= 0 && rtp <= 0xffffffff ? rtp : null,
        presentedFrames: counter,
        displayMs: metadata.expectedDisplayTime,
        receiveMs: validReceive ? metadata.receiveTime! : null,
        decodeMs:
          finite(metadata.processingDuration) && metadata.processingDuration >= 0
            ? metadata.processingDuration * 1000
            : null,
        producerId: producerId ?? null,
        visibility: visible,
        clock: clock ? { ...clock } : null,
        firstSendMs: null,
        lastSendMs: null,
        sendMs: null,
        transitMs: null,
        transitEstimateMs: null,
        backendToDisplayMs: null,
        receiveToDisplayMs: validReceive
          ? metadata.expectedDisplayTime - metadata.receiveTime!
          : null,
        callbackGapMs:
          visible && previousCallback?.visibility ? callbackMs - previousCallback.callbackMs : null,
        missedCallbacks: Math.max(0, gapCount - 1),
        match: visible ? "pending" : "hidden",
      };
      if (sample.match === "pending" && (sample.rtpTimestamp === null || !sample.producerId))
        sample.match = "metadata-unavailable";
      previousCallback = sample;
      match(sample);
      report.samples.push(sample);
      // A minute at <=240 callbacks/s stays bounded even for unusual sources.
      if (report.samples.length >= 14_400) finish("cancelled", "Превышен лимит кадров замера");
    },
    tick(visible: boolean, decodedFps: number | null | undefined) {
      if (!report || (report.status !== "running" && report.status !== "finishing")) return;
      const t = now();
      const from = Math.max(previousTick, started + WARMUP_MS),
        to = Math.min(t, started + DURATION_MS);
      const interval = Math.max(0, to - from);
      if (visible) {
        visibleMs += interval;
        if (finite(decodedFps) && decodedFps >= 0 && from === previousTick && to === t) {
          decodedCount += (decodedFps * interval) / 1000;
          decodedMs += interval;
        }
      } else hiddenMs += interval;
      previousTick = t;
      for (const sample of report.samples)
        if (sample.match === "pending") {
          match(sample);
          if (t - sample.callbackMs > GRACE_MS) sample.match = "timing-unavailable";
        }
      if (t - started >= DURATION_MS) report.status = "finishing";
      report.remainingSeconds = Math.max(0, Math.ceil((DURATION_MS - (t - started)) / 1000));
      if (t - started >= DURATION_MS + GRACE_MS) finish("complete");
      updateSummary();
    },
    stop(reason = "Измерение остановлено") {
      finish("cancelled", reason);
      updateSummary();
    },
    snapshot(): FramePathReport | null {
      return report ? { ...report, samples: report.samples } : null;
    },
  };
}
